import { Modal, notify } from '@carboncopyco/ui';
import {
  type ReactElement,
  cloneElement,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useEditor, useEditorImportEdits } from '../context/EditorSdkProvider';
import { useEditorEvent } from '../hooks/useEditorEvent';
import { useEditorTool } from '../hooks/useEditorTool';

export type ImportManagerToolProps = {
  children: ReactElement<{
    onClose?: () => void;
    onImportEditsSuccess: () => void;
    onImportEditsError: () => void;
  }>;
};

export const ImportManagerTool = memo(
  ({ children }: ImportManagerToolProps) => {
    const editorSdk = useEditor();
    const { isOpen, close } = useEditorTool('importManager');
    const { onImportEdits } = useEditorImportEdits();
    const shouldNotify = useRef(false);
    const [isToolActive, setIsToolActive] = useState(true);

    useEditorEvent(editorSdk, 'onEditorLoaded', () => {
      if (shouldNotify.current) {
        notify.success('Edits imported successfully');
        shouldNotify.current = false;
        close();
      }
    });

    useEffect(() => {
      if (isOpen) {
        setIsToolActive(true);
      }
    }, [isOpen]);

    if (!isToolActive) {
      return null;
    }

    return (
      <Modal
        open={isOpen}
        onClose={close}
      >
        {cloneElement(children, {
          onClose: close,
          onImportEditsSuccess: () => {
            onImportEdits();
            shouldNotify.current = true;
            setIsToolActive(false);
          },
          onImportEditsError: () => {
            close();
            notify.error('Failed to import edits');
          },
        })}
      </Modal>
    );
  }
);

ImportManagerTool.displayName = 'ImportManagerTool';
